<template>
  <modal id="addTemplate"
         v-model="visible"
         :title="templateIdToEdit ? $t('w-templatesForm:editTemplate') : $t('w-templatesForm:createTemplate')"
         :callback="createBpmnw"
         size="lg"
         @modal:cancel="$emit('cancel')"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('w-templatesForm:required') }}</small>

    <div slot="modal-body">
      <h3>{{ $t('w-templatesForm:properties') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="name" class="control-label">{{ $t('w-templatesForm:name') | capitalize }} *</label>
          <input id="name"
                 v-model="name"
                 v-validate="'required|text'"
                 :data-vv-as="$t('w-templatesForm:name')"
                 :class="{ 'is-invalid': errors.has('name') }"
                 :disabled="templateIdToEdit!==''"
                 type="text"
                 name="name"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
          <small v-if="templateIdToEdit" class="form-text text-muted">{{ $t('w-templatesForm:nameNonEditable') | capitalize }}</small>
        </div>

        <div class="form-group col-md-6">
          <label for="category" class="control-label">{{ $t('w-templatesForm:category') | capitalize }} *</label>
          <select id="category"
                  v-model="category"
                  v-validate="'required'"
                  :data-vv-as="$t('w-templatesForm:category')"
                  :class="{ 'is-invalid': errors.has('category') }"
                  class="form-control"
                  name="category"
          >
            <option value="" />
            <option value="GATEWAY">{{ $t('w-templatesForm:gateways') | capitalize }}</option>
            <option value="END_DEVICE">{{ $t('w-templatesForm:endDevices') | capitalize }}</option>
          </select>
          <div class="invalid-feedback">{{ errors.first('category') }}</div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-12">
          <label for="description" class="control-label">{{ $t('w-templatesForm:description') | capitalize }}</label>
          <input id="description"
                 v-model="description"
                 v-validate="'text'"
                 :data-vv-as="$t('w-templatesForm:description')"
                 :class="{ 'is-invalid': errors.has('description') }"
                 type="text"
                 name="description"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('description') }}</div>
        </div>
      </div>

      <h3 class="mt-3">{{ $t('w-templatesForm:webservices') | capitalize }} *</h3>
      <p class="text-muted font-italic">{{ $t('w-templatesForm:workflowHelper') | capitalize }}</p>

      <div class="row webservices-lists">
        <div class="col-md-6">
          <h4 class="d-flex justify-content-between align-items-center">
            {{ $t('w-templatesForm:available') | capitalize }}
            <input id="filterAvailableWebservices" v-model="filterAvailableWebservices" :placeholder="$t('w-templatesForm:filter') | capitalize" type="text" class="form-control form-control-sm">
          </h4>

          <ul class="list-group">
            <draggable v-model="filteredAvailableWebservices" :sort="false" :group="{ name: 'webservices', pull: 'clone', put: false }" class="dragArea">
              <li v-if="filteredAvailableWebservices.length===0" class="text-center text-muted font-italic mt-3">{{ $t('w-templatesForm:noCategory') | capitalize }}</li>
              <li v-for="webservice in filteredAvailableWebservices" v-else :key="webservice.id" class="list-group-item">
                <span :title="webservice.name" class="name">{{ webservice.name }}</span>
                <span v-if="webservice.asynchronous" :title="$t('w-templatesForm:asynchronous') | capitalize" class="icon-clock" />
              </li>
            </draggable>
          </ul>
        </div>

        <div class="col-md-6">
          <h4 class="d-flex align-items-center">{{ $t('w-templatesForm:selected') | capitalize }}</h4>

          <ul class="list-group">
            <draggable v-model="selectedWebservices" :group="{ name: 'webservices' }" class="dragArea">
              <li v-for="(webservice, index) in selectedWebservices" :key="index" class="list-group-item">
                <span :title="$t('w-templatesForm:remove') | capitalize" class="icon-remove mr-2" @click="removeSelected(webservice)" />
                <span :title="webservice.name" class="name">{{ $t('w-templatesForm:task', {index: index+1}) | capitalize }} {{ webservice.name }}</span>
                <span v-if="webservice.asynchronous" :title="$t('w-templatesForm:asynchronous') | capitalize" class="icon-clock" />
              </li>
            </draggable>
          </ul>
          <small v-if="selectedWebservicesEmptyError" class="text-danger">A minimum of one web-service is required</small>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { find, filter, sortBy, isEmpty, each } from 'lodash-es'
import to from '@/modules/await-to-js'
import BpmnW from '@/modules/bpmn/bpmnw'
import eventBus from '@/modules/eventBus'
const Draggable = () => import(/* webpackChunkName: "l-draggable" */ 'vuedraggable')

export default {
  components: { Draggable },

  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    // id of the template to edit
    templateIdToEdit: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,

      // Used for edit
      workflowDto: {},

      name: '',
      category: '',
      description: '',

      filterAvailableWebservices: '',
      availableWebservices: [],
      selectedWebservices: [],
      selectedWebservicesEmptyError: false,
      dtos: [],

      bpmnw: {}
    }
  },

  computed: {
    // Enable/disable save button
    canSaveWebservice () {
      return this.selectedWebservices.length !== 0 && this.name !== '' && this.category !== ''
    },

    // Filter available webservices
    filteredAvailableWebservices () {
      return filter(this.availableWebservices, webservice => webservice.name.toLowerCase().indexOf(this.filterAvailableWebservices.toLowerCase()) > -1)
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show && this.templateIdToEdit) {
        this.getWorkflowTemplate()
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    category (newCategory) {
      this.availableWebservices = []
      this.selectedWebservices = []

      if (newCategory) {
        this.getWebServices()
      }
    },

    selectedWebservices () {
      if (this.availableWebservices.length > 0) {
        if (this.selectedWebservices.length > 0) this.selectedWebservicesEmptyError = false
        else this.selectedWebservicesEmptyError = true
      }
    }
  },

  methods: {
    async reset () {
      this.name = ''
      this.category = ''
      this.description = ''
      this.filterAvailableWebservices = ''
      this.availableWebservices = []
      this.selectedWebservices = []
      this.selectedWebservicesEmptyError = false
      this.dtos = []
      this.bpmnw = {}

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async getWebServices () {
      const search = this.$utils.addSearch([], 'eq', this.category, 'workflowGroups')

      let error, response
      [error, response] = await to(this.$api.getWebServices(this.$store.getters.application, { search: JSON.stringify(search[0]) }))
      if (error || !response) return

      this.availableWebservices = sortBy(response.data.list, 'name')

      // Set selectedWebservices in case of edit
      if (this.templateIdToEdit && !isEmpty(this.workflowDto)) {
        // Get webServices from processVariables and add them to selectedWebservices
        each(this.workflowDto.processVariables, webservice => {
          const selectedWebservice = find(this.availableWebservices, aws => aws.name === webservice.name.variable)
          this.selectedWebservices.push(selectedWebservice)
        })

        // Reset dto, no need anymore
        this.workflowDto = {}
      }

      // Get dtos parameters from webservices
      const dtos = []
      each(this.availableWebservices, webservice => {
        each(webservice.parameters, param => {
          if (param.direction === 'REQUEST' && param.location === 'BODY') {
            dtos.push(param.type)
          }
        })
      })

      // Get dtos and add them in webservice
      let dtosError, dtosResponse
      [dtosError, dtosResponse] = await to(this.$api.getDtos(this.$store.getters.application, { names: dtos.join(',') }))
      if (dtosError) return

      each(this.availableWebservices, webservice => {
        each(webservice.parameters, param => {
          if (param.direction === 'REQUEST' && param.location === 'BODY') {
            param.dto = find(dtosResponse.data.list, dto => dto.name === param.type)
          }
        })
      })
    },

    async getWorkflowTemplate () {
      let error, response
      [error, response] = await to(this.$api.getWorkflowTemplate(this.$store.getters.application, this.templateIdToEdit, { fields: '!xmlB64' }))
      if (error || !response) return

      this.workflowDto = response.data
      this.name = response.data.name
      this.category = response.data.category
      this.description = response.data.description
      // Do not set selectedWebservices list now as availableWebservices may not be already loaded (request if fired thanks to the category watcher)
    },

    // Remove a selected webservice from selected list
    removeSelected (webservice) {
      this.selectedWebservices.splice(this.selectedWebservices.indexOf(webservice), 1)
    },

    // Create BPMN object from JSON object
    async createBpmnw () {
      // Validate form
      let valid = await this.$validator.validateAll()
      // Validate selected webServices
      if (this.selectedWebservices.length === 0) {
        this.selectedWebservicesEmptyError = true
        valid = false
      } else {
        this.selectedWebservicesEmptyError = false
      }

      if (!valid) return

      this.bpmnw = new BpmnW({
        name: this.name,
        category: this.category,
        description: this.description,
        webservices: this.selectedWebservices
      }, this.createBpmnXml)
    },

    // Convert BPMN object to XML
    createBpmnXml () {
      this.bpmnw.exportXml(this.save)
    },

    // Save BPMN XML
    async save (bpmnXml) {
      // Create form data with the file
      const data = new FormData()
      data.append('processName', this.name + '.bpmn20.xml')
      data.append('category', this.category)
      data.append('fileAsString', bpmnXml)
      data.append('versionning', this.templateIdToEdit !== '')

      let error
      [error] = await to(this.$api.createWorkflowTemplate(this.$store.getters.application, data))
      if (error) return

      this.visible = false
      // Success
      if (this.templateIdToEdit) {
        eventBus.$emit('message-success', this.$t('w-templatesForm:editSuccess', { template: this.name }))
      } else {
        eventBus.$emit('message-success', this.$t('w-templatesForm:createSuccess', { template: this.name }))
      }
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .webservices-lists {
    h4 {
      height: 48px;
      padding: 0.6rem 1rem;
      margin-bottom: 0;
      background: $gray-300;
      border-top-left-radius: $border-radius;
      border-top-right-radius: $border-radius;

      input {
        width: 100px;
        transition: all ease 0.35s;

        &:focus {
          width: 200px;
        }
      }
    }

    ul.list-group {
      height: 350px;
      max-height: 350px;
      padding: 0;
      overflow-y: auto;
      border: 1px solid $gray-300;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: $border-radius;
      border-bottom-left-radius: $border-radius;

      // Draggable target
      & > div.dragArea {
        min-height: 100%;
      }

      li.list-group-item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 0.6rem 1rem;
        cursor: move;
        user-select: none;
        border-top: 1px solid $gray-300;
        border-bottom: 1px solid $gray-300;
        border-radius: 0;

        span.name {
          display: block;
          flex: 1;
          // -30px for icon-clock
          max-width: calc(100% - 30px);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .icon-remove {
          font-size: 0.75rem;
        }

        .icon-clock {
          width: 21px;
          margin: -3px 0 0 9px;
          cursor: default;
        }
      }
    }
  }
</style>
